import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Box,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import useSubscription from 'hooks/payments/useSubscription';
import routes from 'common/routes';
import ChargesHistoryTable, { formatChargeDate } from 'components/client-payment/ChargesHistoryTable';
import UpdateCardDialog from 'components/client-payment/UpdateCardDialog';

const GOLD = '#F9BD4E';
const DANGER = '#B21616';

// Dorado para activa (acento pequeño); semánticos para el resto.
const STATUS_STYLE = {
  active: { bg: 'rgba(249,189,78,0.14)', color: GOLD },
  grace_period: { bg: 'rgba(235,189,92,0.18)', color: '#EBBD5C' },
  pending: { bg: 'var(--otx-surface-hover)', color: 'text.secondary' },
  canceled: { bg: 'rgba(154,160,166,0.18)', color: '#c4c8cc' },
};

const KNOWN_STATUSES = ['active', 'grace_period', 'pending', 'canceled'];

function MySubscriptionCard({ onResubscribe = null }) {
  const { t } = useTranslation(['payments']);
  const navigate = useNavigate();
  const { id: userId } = useSelector((state) => state.auth.user);
  const { fetchMySubscription, cancelSubscription, retryPayment } = useSubscription(userId);

  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSubscription(await fetchMySubscription());
    } catch {
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [fetchMySubscription]);

  useEffect(() => { load(); }, [load]);

  const confirmCancel = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      setSubscription(await cancelSubscription(subscription.id));
    } finally {
      setBusy(false);
    }
  };

  // Reintenta el cobro con la MISMA tarjeta (caso: el rechazo fue temporal).
  const handleRetry = async () => {
    setRetrying(true);
    try {
      setSubscription(await retryPayment(subscription.id));
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" py={3}><CircularProgress size={26} sx={{ color: GOLD }} /></Stack>
    );
  }

  // Guardas: nada que mostrar si no hay datos válidos (evita "$NaN" / estado roto).
  const amount = Number(subscription?.amount);
  const valid = subscription
    && Number.isFinite(amount) && amount > 0
    && subscription.currency
    && KNOWN_STATUSES.includes(subscription.status);

  if (!valid) return null;

  const style = STATUS_STYLE[subscription.status] || STATUS_STYLE.pending;
  const canManage = ['active', 'grace_period', 'pending'].includes(subscription.status);
  const isWompi = subscription.provider === 'wompi';

  return (
    <>
      <Card
        sx={{
          borderRadius: 3,
          mb: 3,
          border: '1px solid',
          borderColor: 'hairline.main',
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
        }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutorenewIcon sx={{ color: GOLD }} />
              <Typography variant="h6" fontWeight={800} color="text.primary">{t('subscription.manage.title')}</Typography>
            </Stack>
            <Chip
              label={t(`subscription.manage.statuses.${subscription.status}`, { defaultValue: subscription.status })}
              size="small"
              sx={{ backgroundColor: style.bg, color: style.color, fontWeight: 800, letterSpacing: 0.3 }}
            />
          </Stack>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{subscription.plan_name}</Typography>

          <Stack spacing={1.25}>
            <Row label={t('subscription.manage.amount')} value={(
              <span><b style={{ color: GOLD }}>${amount.toLocaleString('es-CO')}</b> {subscription.currency}{t('subscription.perMonthSuffix')}</span>
            )} />
            <Row
              label={t('subscription.manage.renews')}
              value={t(`subscription.manage.interval.${subscription.interval}`, {
                defaultValue: subscription.interval || t('subscription.manage.interval.month')
              })}
            />
            <Row label={t('subscription.manage.nextCharge')} value={formatChargeDate(subscription.next_charge_date)} />
            {subscription.card_last4 && (
              <Row label={t('subscription.manage.card')} value={(
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <CreditCardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <span>{subscription.card_brand} •••• {subscription.card_last4}</span>
                </Stack>
              )} />
            )}
          </Stack>

          {/* Suscripción cancelada: permitir reactivar (volver a elegir/pagar un plan). */}
          {subscription.status === 'canceled' && onResubscribe && (
            <>
              <Divider sx={{ my: 2, borderColor: 'hairline.main' }} />
              <Button
                fullWidth
                onClick={onResubscribe}
                sx={{ py: 1.1, borderRadius: 2, fontWeight: 800, background: 'var(--otx-cta-bg)', color: 'var(--otx-cta-text)', '&:hover': { background: 'var(--otx-cta-bg)', filter: 'brightness(0.94)' } }}>
                {t('subscription.manage.resubscribe', { defaultValue: 'Reactivar suscripción' })}
              </Button>
            </>
          )}

          {canManage && (
            <>
              <Divider sx={{ my: 2, borderColor: 'hairline.main' }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                {/* En gracia: reintentar con la MISMA tarjeta (CTA blanco). */}
                {isWompi && subscription.status === 'grace_period' && (
                  <Button fullWidth disabled={busy || retrying} onClick={handleRetry}
                    sx={{ py: 1.1, borderRadius: 2, fontWeight: 800, background: 'var(--otx-cta-bg)', color: 'var(--otx-cta-text)', '&:hover': { background: 'var(--otx-cta-bg)', filter: 'brightness(0.94)' }, '&.Mui-disabled': { background: 'var(--otx-btn-disabled-bg)', color: 'var(--otx-btn-disabled-text)' } }}>
                    {retrying ? <CircularProgress size={20} sx={{ color: 'var(--otx-cta-text)' }} /> : t('subscription.manage.retryPayment', { defaultValue: 'Reintentar pago' })}
                  </Button>
                )}
                {/* Actualizar tarjeta: acción secundaria neutra (outline monocromo). */}
                {isWompi && (
                  <Button variant="outlined" fullWidth disabled={busy || retrying}
                    sx={{ color: 'text.primary', borderColor: 'hairline.strong', fontWeight: 700, '&:hover': { borderColor: 'hairline.strong', background: 'var(--otx-surface-hover)' } }}
                    onClick={() => setCardOpen(true)}>
                    {t('subscription.manage.updateCard')}
                  </Button>
                )}
                {subscription.status !== 'canceled' && (
                  <Button fullWidth disabled={busy || retrying} onClick={() => setConfirmOpen(true)}
                    sx={{
                      color: 'text.primary',
                      border: '1px solid',
                      borderColor: 'hairline.strong',
                      fontWeight: 700,
                      transition: 'background-color 200ms cubic-bezier(0.32,0.72,0,1), color 200ms cubic-bezier(0.32,0.72,0,1), border-color 200ms cubic-bezier(0.32,0.72,0,1)',
                      '&:hover': { backgroundColor: DANGER, borderColor: DANGER, color: '#fff' },
                    }}>
                    {t('subscription.manage.cancel')}
                  </Button>
                )}
              </Stack>
            </>
          )}

          {/* Último cobro + acceso al historial completo (página "Historial de cobros"). */}
          {subscription.recent_charges?.length > 0 && (
            <>
              <Divider sx={{ my: 2, borderColor: 'hairline.main' }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                  {t('subscription.manage.lastCharge', { defaultValue: 'Último cobro' })}
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  onClick={() => navigate(routes.clientChargesHistory.path)}
                  sx={{ color: GOLD, fontWeight: 700, textTransform: 'none', '&:hover': { backgroundColor: 'rgba(249,189,78,0.08)' } }}>
                  {t('subscription.manage.viewFullHistory', { defaultValue: 'Ver historial completo' })}
                </Button>
              </Stack>
              <ChargesHistoryTable charges={subscription.recent_charges.slice(0, 1)} t={t} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Diálogo: actualizar tarjeta (Wompi) — componente compartido */}
      <UpdateCardDialog
        open={cardOpen}
        onClose={() => setCardOpen(false)}
        subscription={subscription}
        onUpdated={setSubscription}
      />

      {/* Confirmación de cancelación (modal monocromo, no window.confirm) */}
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmCancel}
        title={t('subscription.manage.cancelTitle', { defaultValue: 'Cancelar suscripción' })}
        message={t('subscription.manage.cancelConfirm')}
        confirmLabel={t('subscription.manage.cancelCta', { defaultValue: 'Sí, cancelar' })}
        cancelLabel={t('subscription.manage.keepCta', { defaultValue: 'Volver' })}
        note=""
      />
    </>
  );
}

function Row({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Box component="span" sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary' }}>{value}</Box>
    </Stack>
  );
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

MySubscriptionCard.propTypes = {
  onResubscribe: PropTypes.func,
};

export default MySubscriptionCard;
